import React, { useState, useCallback, useRef, useEffect } from 'react';
import { toBlob } from 'html-to-image';
import { RANKS, getHandName, getContrastColor } from '../api/rangesApi';
import '../styles/RangeGrid.css';

const RangeGrid = ({ grid, actions, selectedAction, onGridChange, readOnly }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragMode, setDragMode] = useState(null); // 'apply' или 'clear'
  const [tooltip, setTooltip] = useState({ visible: false, hand: '', x: 0, y: 0 });
  const [isCopying, setIsCopying] = useState(false);
  const [copyStatus, setCopyStatus] = useState(null); // 'success' | 'error' | null
  const captureRef = useRef(null);
  const statusTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (statusTimeoutRef.current) {
        clearTimeout(statusTimeoutRef.current);
      }
    };
  }, []);

  // Получить цвет ячейки на основе распределения действий
  const getCellStyle = (hand) => {
    const handData = grid[hand] || {};
    const foldPercent = handData.fold ?? 100;
    
    // Если 100% fold - серый фон
    if (foldPercent === 100) {
      return { background: '#2d3436', color: '#ffffff' };
    }
    
    // Находим доминирующий цвет (действие с наибольшим процентом)
    let dominantColor = '#2d3436';
    let maxPercent = 0;
    
    // Создаем градиент из секторов динамически
    const gradientParts = [];
    let currentPercent = 0;
    
    actions.forEach(action => {
      const actionPercent = handData[action.id] || 0;
      if (actionPercent > 0) {
        gradientParts.push(`${action.color} ${currentPercent}%`);
        currentPercent += actionPercent;
        gradientParts.push(`${action.color} ${currentPercent}%`);
        
        // Определяем доминирующий цвет
        if (actionPercent > maxPercent) {
          maxPercent = actionPercent;
          dominantColor = action.color;
        }
      }
    });
    
    const textColor = getContrastColor(dominantColor);
    
    if (gradientParts.length <= 2) {
      return { 
        background: gradientParts[0]?.split(' ')[0] || '#2d3436',
        color: textColor
      };
    }
    
    return {
      background: `linear-gradient(135deg, ${gradientParts.join(', ')})`,
      color: textColor
    };
  };

  // Применить действие к руке
  const applyAction = useCallback((hand, mode) => {
    let newData = {};
    
    if (mode === 'apply') {
      // Применяем текущие проценты из селектора
      actions.forEach(action => {
        newData[action.id] = action.percentage || 0;
      });
    } else {
      // Сбрасываем в fold
      actions.forEach(action => {
        newData[action.id] = action.id === 'fold' ? 100 : 0;
      });
    }
    
    onGridChange({
      ...grid,
      [hand]: newData
    });
  }, [grid, actions, onGridChange]);

  const handleMouseDown = (hand, e) => {
    if (readOnly) return;
    e.preventDefault();
    setIsDragging(true);
    
    const currentData = grid[hand] || {};
    // Если уже раскрашено - будем очищать, иначе - применять
    const foldPercent = currentData.fold ?? 100;
    const isColored = foldPercent < 100;
    const mode = isColored ? 'clear' : 'apply';
    
    setDragMode(mode);
    applyAction(hand, mode);
  };

  const handleMouseEnter = (hand, e) => {
    if (!readOnly && isDragging && dragMode) {
      applyAction(hand, dragMode);
    }
    
    // Показываем тултип
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      visible: true,
      hand,
      x: rect.left + rect.width / 2,
      y: rect.top
    });
  };

  const handleMouseLeaveCell = () => {
    setTooltip({ ...tooltip, visible: false });
  };

  // Получить данные для тултипа
  const getTooltipContent = (hand) => {
    const handData = grid[hand] || { allin: 0, raise: 0, fold: 100 };
    return actions.map(action => ({
      name: action.name,
      color: action.color,
      percentage: handData[action.id] || 0
    })).filter(item => item.percentage > 0);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setDragMode(null);
  };

  const showCopyStatus = (status) => {
    setCopyStatus(status);
    if (statusTimeoutRef.current) {
      clearTimeout(statusTimeoutRef.current);
    }
    statusTimeoutRef.current = setTimeout(() => {
      setCopyStatus(null);
    }, 2000);
  };

  const handleCopyRange = async () => {
    if (!captureRef.current || isCopying) return;
    setIsCopying(true);
    setCopyStatus(null);

    try {
      const blob = await toBlob(captureRef.current, {
        backgroundColor: '#0f1115'
      });

      if (!blob) {
        throw new Error('Не удалось создать изображение');
      }

      await navigator.clipboard.write([
        new ClipboardItem({ [blob.type]: blob })
      ]);

      showCopyStatus('success');
    } catch (error) {
      showCopyStatus('error');
    } finally {
      setIsCopying(false);
    }
  };

  // Рассчитываем общий процент диапазона
  const calculateTotalRange = () => {
    let total = 0;
    
    Object.entries(grid).forEach(([hand, data]) => {
      const weight = hand.length === 2 ? 6 : (hand.endsWith('s') ? 4 : 12);
      const foldPercent = data?.fold ?? 100;
      const playPercent = 100 - foldPercent;
      total += (playPercent / 100) * weight;
    });
    
    return ((total / 1326) * 100).toFixed(1);
  };

  const tooltipData = tooltip.visible ? getTooltipContent(tooltip.hand) : [];

  return (
    <div 
      className="range-grid-container"
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      {/* Тултип */}
      {tooltip.visible && (
        <div 
          className="cell-tooltip"
          style={{
            left: tooltip.x,
            top: tooltip.y
          }}
        >
          <div className="tooltip-header">{tooltip.hand}</div>
          <div className="tooltip-content">
            {tooltipData.length > 0 ? (
              tooltipData.map((item, idx) => (
                <div key={idx} className="tooltip-row">
                  <span 
                    className="tooltip-color" 
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="tooltip-name">{item.name}</span>
                  <span className="tooltip-percent">{item.percentage}%</span>
                </div>
              ))
            ) : (
              <div className="tooltip-row tooltip-fold">
                <span className="tooltip-name">Fold</span>
                <span className="tooltip-percent">100%</span>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="range-grid-capture" ref={captureRef}>
        <div className="range-grid">
          {RANKS.map((_, rowIndex) => (
            <div key={rowIndex} className="grid-row">
              {RANKS.map((_, colIndex) => {
                const hand = getHandName(rowIndex, colIndex);
                const isPair = rowIndex === colIndex;
                const isSuited = rowIndex < colIndex;
                
                return (
                  <div
                    key={hand}
                    className={`grid-cell ${isPair ? 'pair' : ''} ${isSuited ? 'suited' : 'offsuit'}`}
                    style={getCellStyle(hand)}
                    onMouseDown={(e) => handleMouseDown(hand, e)}
                    onMouseEnter={(e) => handleMouseEnter(hand, e)}
                    onMouseLeave={handleMouseLeaveCell}
                  >
                    <span className="hand-label">{hand}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        
        <div className="range-stats">
          <div className="range-summary">
            <div className="total-range">
              Total Range <span className="range-value">{calculateTotalRange()}%</span>
            </div>
            <div className="copy-range">
              <button
                type="button"
                className="copy-range-btn"
                onClick={handleCopyRange}
                disabled={isCopying}
              >
                {isCopying ? 'Копирование...' : 'Скопировать'}
              </button>
              {copyStatus === 'success' && (
                <span className="copy-range-status success">Скопировано</span>
              )}
              {copyStatus === 'error' && (
                <span className="copy-range-status error">Ошибка копирования</span>
              )}
            </div>
          </div>
          <div className="action-legend">
            {actions.map(action => (
              <div key={action.id} className="legend-item">
                <span 
                  className="legend-color" 
                  style={{ backgroundColor: action.color }}
                />
                <span className="legend-name">{action.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RangeGrid;

